import './style.scss';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import {
  getControlSocialActivities,
  getOrCreateAdvisorPhaseMeeting,
  getMunicipalities,
  getSchools,
  getUsers
} from '../../API/apiService';
import CreateAdvisory from '../../components/adminSchools/createAdvisory';
import CreateSocialControl from '../../components/adminSchools/createSocialControl';
import ModalContainer from '../../components/modalContainer';
import MenuItem from '../../components/schoolsDinam/menuItem';
import WelcomeUser from '../../components/welcomeUser';
import { constants } from '../../config/constants';

const COP_ADVISORY_TYPE_OPTIONS = [
  { name: 'Asesoría', id: 'ASESORIA' },
  { name: 'Comité Pedagógico', id: 'COMITE_PEDAGOGICO' },
  { name: 'Evento', id: 'EVENTO' },
  { name: 'Otro', id: 'OTRO' }
];

const HIDDEN_SCHOOLS_STORAGE_PREFIX = 'hidden-schools';

function getSchoolVisibilityId(school) {
  return String(
    school?.id ?? school?.idActivity ?? school?.code ?? ''
  ).trim();
}

function readHiddenSchoolIds(storageKey) {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return Array.isArray(parsed)
      ? parsed.map(value => String(value || '').trim()).filter(Boolean)
      : [];
  } catch (error) {
    return [];
  }
}

function writeHiddenSchoolIds(storageKey, hiddenIds) {
  localStorage.setItem(storageKey, JSON.stringify(hiddenIds));
}

function PrincipalMenu() {
  const userId = localStorage.getItem(constants.USER_ID);
  const userName = localStorage.getItem(constants.USERNAME);
  const userRole = localStorage.getItem(constants.ROLE);
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [municipalities, setMunicipalities] = useState([]);
  const [users, setUsers] = useState([]);
  const [showHiddenSchools, setShowHiddenSchools] = useState(false);
  const normalizedUserRole = String(userRole || '')
    .trim()
    .toLowerCase();
  const isMediatorCop = normalizedUserRole === 'mediador cop';
  const isSocialControlMediator =
    normalizedUserRole === 'mediador control social';
  const schoolType = COP_ADVISORY_TYPE_OPTIONS;
  const hiddenStorageKey = `${HIDDEN_SCHOOLS_STORAGE_PREFIX}:${
    userId || 'anon'
  }:${
    isMediatorCop ? 'cop' : isSocialControlMediator ? 'social-control' : 'pf'
  }`;
  const [hiddenSchoolIds, setHiddenSchoolIds] = useState(() =>
    readHiddenSchoolIds(hiddenStorageKey)
  );
  const processLabel = isMediatorCop
    ? 'Asesoria'
    : isSocialControlMediator
    ? 'Actividad'
    : 'Proceso Formativo';
  const hiddenSchoolIdSet = useMemo(
    () => new Set(hiddenSchoolIds),
    [hiddenSchoolIds]
  );
  const visibleSchools = useMemo(
    () =>
      schools.filter(
        school => !hiddenSchoolIdSet.has(getSchoolVisibilityId(school))
      ),
    [schools, hiddenSchoolIdSet]
  );
  const hiddenSchools = useMemo(
    () =>
      schools.filter(school =>
        hiddenSchoolIdSet.has(getSchoolVisibilityId(school))
      ),
    [schools, hiddenSchoolIdSet]
  );
  const displayedSchools = showHiddenSchools ? hiddenSchools : visibleSchools;

  function mapUsersResponse(list) {
    if (!Array.isArray(list)) return [];

    const usersMap = new Map();

    list.forEach(user => {
      if (!user?.id) return;

      const normalizedName = String(
        user.full_name || user.username || ''
      ).trim();
      if (!normalizedName) return;

      usersMap.set(user.id, {
        id: user.id,
        name: normalizedName,
        roleName: user.role?.name,
        roleId: user.role?.id,
        active: user.active === undefined ? true : Boolean(user.active)
      });
    });

    return Array.from(usersMap.values());
  }

  async function getSchoolsInfo() {
    if (!userId || userId === 'null') {
      setSchools([]);
      return;
    }

    const data = isSocialControlMediator
      ? await getControlSocialActivities()
      : await getSchools(Number(userId));
    const schoolsList = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : [];

    setSchools(
      isSocialControlMediator
        ? schoolsList.map(activity => ({
            ...activity,
            id: activity.idActivity
          }))
        : schoolsList.filter(school => school?.active === true)
    );
  }

  async function loadRoleOptions() {
    if (!isMediatorCop && !isSocialControlMediator) return;

    const municipalitiesResponse = await getMunicipalities();

    setMunicipalities(
      Array.isArray(municipalitiesResponse) ? municipalitiesResponse : []
    );

    if (isMediatorCop) {
      const usersResponse = await getUsers({ noCache: true });
      setUsers(mapUsersResponse(usersResponse));
    }
  }

  async function getBasicInfo() {
    await getSchoolsInfo();

    if (isMediatorCop || isSocialControlMediator) {
      await loadRoleOptions();
      return;
    }

    setMunicipalities([]);
    setUsers([]);
  }

  useEffect(() => {
    if (!userId || userId === 'null') {
      setSchools([]);
      return;
    }

    getBasicInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isMediatorCop, isSocialControlMediator]);

  useEffect(() => {
    setHiddenSchoolIds(readHiddenSchoolIds(hiddenStorageKey));
    setShowHiddenSchools(false);
  }, [hiddenStorageKey]);

  function hideSchool(school) {
    const schoolId = getSchoolVisibilityId(school);
    if (!schoolId) return;

    setHiddenSchoolIds(previousIds => {
      const nextIds = previousIds.includes(schoolId)
        ? previousIds
        : [...previousIds, schoolId];
      writeHiddenSchoolIds(hiddenStorageKey, nextIds);
      return nextIds;
    });
    toast(`${processLabel} oculto`, { type: 'success' });
  }

  function showSchool(school) {
    const schoolId = getSchoolVisibilityId(school);
    if (!schoolId) return;

    setHiddenSchoolIds(previousIds => {
      const nextIds = previousIds.filter(currentId => currentId !== schoolId);
      writeHiddenSchoolIds(hiddenStorageKey, nextIds);
      return nextIds;
    });
    toast(`${processLabel} visible nuevamente`, { type: 'success' });
  }

  async function goToSocializationEdit(
    school,
    phase = null,
    meetingOrder = null
  ) {
    if (isMediatorCop && phase && meetingOrder) {
      const meeting = await getOrCreateAdvisorPhaseMeeting({
        schoolId: Number(school?.id),
        phase,
        order: Number(meetingOrder),
        userId: Number(userId)
      });

      const meetingGroupId = meeting?.meetingGroupId ?? meeting?.id;
      if (!meetingGroupId) return;

      const params = new URLSearchParams({
        encuentro: String(meetingGroupId),
        phase,
        order: String(meetingOrder)
      });

      navigate(
        `/escuelas/grupo/lista_de_encuentros/encuentro?${params.toString()}`,
        {
          state: {
            backTo: '/escuelas',
            school,
            advisorPhase: phase,
            advisorMeetingOrder: Number(meetingOrder)
          }
        }
      );
      return;
    }

    const phaseSearch = meetingOrder
      ? `?number=${encodeURIComponent(meetingOrder)}`
      : '';

    navigate(`/socializacion/${school.id}${phaseSearch}`, {
      state: {
        backTo: '/escuelas',
        ...(meetingOrder ? { socializationNumber: Number(meetingOrder) } : {})
      }
    });
  }

  async function handleOpenCreateSchool() {
    if (isMediatorCop || isSocialControlMediator) {
      await loadRoleOptions();
    }

    setOpenModal(true);
  }

  async function handleCreateSchool(created) {
    if (!created) return;

    if (isSocialControlMediator) {
      toast('Actividad de Control Social creada exitosamente!', {
        type: 'success'
      });
      setOpenModal(false);
      await getSchoolsInfo();
      return;
    }

    toast('Asesoría creada exitosamente!', { type: 'success' });
    await getSchoolsInfo();
  }

  function getEmptySchoolsMessage() {
    if (showHiddenSchools) {
      return `No tienes ${
        isMediatorCop
          ? 'asesorias'
          : isSocialControlMediator
          ? 'actividades'
          : 'procesos formativos'
      } ocultos.`;
    }

    return isMediatorCop
      ? 'Aun no tienes Asesorias para mostrar.'
      : isSocialControlMediator
      ? 'Aún no hay actividades de Control Social'
      : 'Aun no hay Proceso Formativo';
  }

  return (
    <>
      {openModal && (isMediatorCop || isSocialControlMediator) && (
        <ModalContainer>
          {isMediatorCop ? (
            <CreateAdvisory
              municipalities={municipalities}
              schoolType={schoolType}
              setOpenModal={setOpenModal}
              users={users}
              created={handleCreateSchool}
              currentMediatorId={userId}
              currentMediatorName={userName}
            />
          ) : (
            <CreateSocialControl
              setOpenModal={setOpenModal}
              created={handleCreateSchool}
            />
          )}
        </ModalContainer>
      )}

      <section
        className={`schools${isMediatorCop ? ' schools--mediator-cop' : ''}`}>
        <div className='schools__content content'>
          <div className='content__welcome'>
            <WelcomeUser
              schoolsList={true}
              action={
                isSocialControlMediator ? (
                  <button
                    type='button'
                    className='schools-add-activity'
                    onClick={handleOpenCreateSchool}>
                    <span
                      className='material-symbols-rounded schools-add-activity__icon'
                      aria-hidden='true'>
                      add
                    </span>
                    <span>Agregar actividad</span>
                  </button>
                ) : null
              }
            />

            {isMediatorCop && (
              <div className='schools-header'>
                <div className='schools-header__text'>
                  <h2 className='schools-header__title'>
                    Gestiona tus Asesorías
                  </h2>
                  <p className='schools-header__subtitle'>
                    Crea una asesoría, descarga beneficiarios y entra a sus
                    evidencias.
                  </p>
                </div>

                <button
                  type='button'
                  className='schools-header__action'
                  onClick={handleOpenCreateSchool}>
                  <span className='schools-header__icon' aria-hidden='true'>
                    +
                  </span>
                  <span>Agregar Asesoría</span>
                </button>
              </div>
            )}
          </div>

          <div className='content__wrapper'>
            <div className='schools-visibility-bar'>
              <div className='schools-visibility-bar__actions'>
                <button
                  type='button'
                  className={`schools-hidden-toggle${
                    showHiddenSchools ? ' schools-hidden-toggle--active' : ''
                  }`}
                  onClick={() => setShowHiddenSchools(previous => !previous)}
                  disabled={!showHiddenSchools && hiddenSchools.length === 0}>
                  <span
                    className='material-symbols-rounded schools-hidden-toggle__icon'
                    aria-hidden='true'>
                    {showHiddenSchools ? 'visibility' : 'visibility_off'}
                  </span>
                  <span>
                    {showHiddenSchools
                      ? 'Ver visibles'
                      : `Ocultos (${hiddenSchools.length})`}
                  </span>
                </button>
                <button
                  type='button'
                  className='schools-incidence-action'
                  onClick={() => navigate('/acciones-incidencia')}>
                  <span className='material-symbols-rounded' aria-hidden='true'>campaign</span>
                  <span>Acciones de incidencia</span>
                </button>
                <button
                  type='button'
                  className='schools-incidence-action schools-requirements-action'
                  onClick={() => navigate('/requerimientos')}>
                  <span className='material-symbols-rounded' aria-hidden='true'>inventory_2</span>
                  <span>Requerimientos</span>
                </button>
              </div>
              <span className='schools-visibility-bar__count'>
                {showHiddenSchools
                  ? `Ocultos ${hiddenSchools.length}`
                  : `Visibles ${visibleSchools.length}`}
              </span>
            </div>

            {displayedSchools.length > 0 ? (
              displayedSchools.map((school, i) => (
                <MenuItem
                  key={`${
                    showHiddenSchools ? 'hidden' : 'visible'
                  }-${getSchoolVisibilityId(school)}`}
                  uid={i}
                  school={school}
                  onEditSocialization={goToSocializationEdit}
                  onGroupCreated={getBasicInfo}
                  isMediatorCop={isMediatorCop}
                  isSocialControlMediator={isSocialControlMediator}
                  municipalities={municipalities}
                  visibilityAction={showHiddenSchools ? 'show' : 'hide'}
                  onToggleVisibility={
                    showHiddenSchools ? showSchool : hideSchool
                  }
                />
              ))
            ) : (
              <p style={{ padding: '1rem' }}>{getEmptySchoolsMessage()}</p>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default PrincipalMenu;
